import { Head, usePage } from "@inertiajs/react"
import { AppLayout } from "@/layouts/app-layout"
import { PageHeader } from "@/hooks/use-page-header"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardFooter } from "@/components/ui/card"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import { Pagination } from "@/components/pagination"
import { EmptyState } from "@/components/empty-state"
import { TeamFormDialog } from "@/components/team-form-dialog"
import { useDebouncedSearch } from "@/hooks/use-debounced-search"
import { Plus, Search, Pencil, UsersRound } from "lucide-react"
import { useState } from "react"
import type { Team, PagyMetadata, SharedProps } from "@/types"

type Props = {
  teams: Team[]
  filters: Record<string, string>
  pagy: PagyMetadata
  organisations: { id: number | string; name: string }[]
  all_teams: { id: number | string; name: string; organisation_id: number | string }[]
}

function TeamsIndex({ teams, filters, pagy, organisations, all_teams }: Props) {
  const { current_user } = usePage<SharedProps>().props
  const canCreate = current_user?.is_super_admin || current_user?.role === "admin"

  const [search, setSearch, searchRef] = useDebouncedSearch(filters.name_cont || "", "/teams", "name_cont")

  const [showForm, setShowForm] = useState(false)
  const [editingTeam, setEditingTeam] = useState<Team | null>(null)

  return (
    <>
      <Head title="Équipes" />
      <PageHeader title="Équipes" />
      <div className="space-y-6">
        <div className="flex items-center justify-between gap-4">
          <div className="relative max-w-sm flex-1">
            <Search className="text-muted-foreground absolute left-3 top-1/2 size-4 -translate-y-1/2" />
            <Input
              ref={searchRef}
              placeholder="Rechercher par nom..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="secondary">
              {pagy.count} résultat{pagy.count > 1 ? "s" : ""}
            </Badge>
            {canCreate && (
              <Button
                onClick={() => {
                  setEditingTeam(null)
                  setShowForm(true)
                }}
              >
                <Plus className="size-4" />
                Nouvelle équipe
              </Button>
            )}
          </div>
        </div>

        <Card>
          <CardContent className="px-0">
            {teams.length === 0 ? (
              <EmptyState
                icon={UsersRound}
                title="Aucune équipe trouvée"
                description="Essayez de modifier vos critères de recherche."
              />
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Nom</TableHead>
                    <TableHead>Organisation</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {teams.map((team) => (
                    <TableRow key={team.id}>
                      <TableCell className="font-medium">{team.name}</TableCell>
                      <TableCell>{team.organisation?.name}</TableCell>
                      <TableCell className="text-right">
                        {canCreate && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setEditingTeam(team)
                              setShowForm(true)
                            }}
                            title="Modifier"
                          >
                            <Pencil className="size-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
          <CardFooter>
            <Pagination pagy={pagy} baseUrl="/teams" filters={filters} className="w-full p-0" />
          </CardFooter>
        </Card>
      </div>

      <TeamFormDialog
        open={showForm}
        onOpenChange={setShowForm}
        team={editingTeam}
        organisations={organisations || []}
        allTeams={all_teams || []}
      />
    </>
  )
}

TeamsIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>

export default TeamsIndex
